<script lang="ts" setup>
  import ArrowUpBoldBoxOutlineIcon from 'vue-material-design-icons/ArrowUpBoldBoxOutline.vue'
  import ButtonDefault from '@/elements/ButtonDefault.vue'
  import { useVersionCheck } from '@/composables/useVersionCheck.ts'
  import { usePwa } from '@/composables/usePwa.ts'
  import Icon from '@/elements/Icon.vue'

  const { anyVersionMissmatch, versionMissmatch, buildMissmatch, apiHashMissmatch, apiHashExpected, apiHashRemote, versionRemote, buildRemote, doUpdate } = useVersionCheck()
  const { isSupported, channel, post, close, error, isClosed, data } = useBroadcastChannel({ name: 'titrop-update-channel' })

  const { isReloadPending } = usePwa()
  const isUpdating = ref(false)
  const updatingTimeLeft = ref(5)
  let btnUpdateTextInterval: number | undefined = undefined

  async function cmdDoUpdate(broadcast = true) {
    console.log('Invoking self update')
    isUpdating.value = true
    btnUpdateTextInterval = window.setInterval(() => {
      updatingTimeLeft.value--
      if (updatingTimeLeft.value == 0) window.clearInterval(btnUpdateTextInterval)
    }, 1000)
    if (broadcast && isSupported.value) {
      console.log('Send doUpdate broadcast')
      post('doUpdate')
    }
    doUpdate()
  }

  watch(data, (x) => {
    if (x == 'doUpdate') {
      console.log('Received doUpdate broadcast')
      void cmdDoUpdate(false)
    }
  })

  const btnUpdateText = computed(() => {
    if (!isUpdating.value) return 'Jetzt aktualisieren'

    if (updatingTimeLeft.value > 0) return `${updatingTimeLeft.value}...`
    return 'Neu laden...'
  })
</script>

<template>
  <div>
    <div
      v-if="$titrop?.user?.roles?.admin && isReloadPending"
      class="bg-orange-600 text-center py-1 sm:py-2 px-2 sm:px-4 items-center text-indigo-100 leading-none w-full">
      Service-Worker updated!
    </div>
    <!-- Backend-Check -->
    <div
      v-if="$user?.roles?.developer && apiHashMissmatch"
      class="bg-orange-600 text-center py-1 sm:py-2 px-2 sm:px-4 items-center text-xs text-indigo-100 leading-none w-full flex">
      <span class="font-bold items-center">
        <Icon
          class="inline"
          icon="RoleDevIcon" />
        [Nur für Developer sichtbar]
      </span>
      <span class="flex-1 text-right">Die Backend/API-Version stimmt nicht überein! Entweder fehlt ein Update oder die Stores müssen regeneriert werden! (Ist: {{ apiHashRemote }}, Erwartet: {{ apiHashExpected }})</span>
    </div>
    <!-- Version/Build-Check -->
    <div
      v-if="anyVersionMissmatch"
      class="bg-indigo-900 text-center pb-2 mx-auto">
      <div
        class="py-1 sm:py-2 px-2 sm:px-4 bg-indigo-800 items-center text-indigo-100 leading-none sm:rounded-full inline-flex w-full sm:max-w-[80vw] sm:min-w-[80vw]"
        role="alert">
        <span class="flex-none rounded-full bg-indigo-500 uppercase px-2 py-1 text-xs font-bold mr-3">Update</span>
        <span class="flex-auto font-semibold mr-2 items-center flex flex-col flex-wrap gap-1">
          <span class="flex-auto text-xs sm:text-base">Eine neue Version ist verfügbar!</span>
          <span class="hidden sm:flex flex-row text-xs">
            (Version:&nbsp;
            <span :class="{ 'font-bold underline': versionMissmatch }">
              {{ versionRemote }}
            </span>
            <ArrowUpBoldBoxOutlineIcon
              v-if="versionMissmatch"
              size="16" />
            , Build:&nbsp;
            <span :class="{ 'font-bold underline': !versionMissmatch && buildMissmatch }">{{ $formatter.DateTime(buildRemote, { omitTodayDate: true }) }}</span>
            <ArrowUpBoldBoxOutlineIcon
              v-if="!versionMissmatch && buildMissmatch"
              size="16" />
            )
          </span>
          <span class="flex-auto font-semibold text-orange-500 mr-2 text-xxs sm:text-xs items-center flex flex-col flex-wrap gap-1">Das Update kann fehlschlagen, wenn mehreren Tabs/Fenster geöffnet sind!</span>
        </span>

        <ButtonDefault
          :rounded="true"
          :disabled="isUpdating"
          class="flex-none h-full text-xs sm:text-base !rounded-full"
          :label="btnUpdateText"
          @click="() => cmdDoUpdate()" />
      </div>
    </div>
  </div>
</template>
